import React, { useEffect, useRef, useState } from 'react';
import PortadaTienda from './PortadaTienda';
import { optimizarFoto } from '../utils/imagen';
import {
  PLANTILLAS, FONDOS, FUENTES, BOTONES, ENFOQUES, ESQUINAS, ALINEACIONES,
  normalizarDiseno, variablesDiseno, SUBTITULO_DEFECTO, BOTON_DEFECTO,
} from '../utils/tiendaDiseno';
import './catalogo.css';

const caja = { marginBottom: '16px', padding: '16px', background: '#FAF8FF', border: '1px solid #EDE9FE', borderRadius: '14px' };
const titulo = { display: 'block', margin: '16px 0 8px', fontSize: '13px', fontWeight: 700, color: '#374151' };
const ayuda = { margin: '5px 0 0', fontSize: '12px', color: '#9CA3AF' };
const input = { width: '100%', padding: '11px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit' };
const chip = (activo) => ({
  padding: '7px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  border: `1.5px solid ${activo ? 'var(--color-primario,#DB2777)' : '#E5E7EB'}`,
  background: activo ? 'color-mix(in srgb, var(--color-primario,#DB2777) 10%, white)' : 'white',
  color: activo ? 'var(--color-primario,#DB2777)' : '#374151',
});
const tarjeta = (activo) => ({
  padding: '8px', borderRadius: '12px', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', background: 'white',
  border: `2px solid ${activo ? 'var(--color-primario,#DB2777)' : '#ECE8F6'}`,
  boxShadow: activo ? '0 0 0 3px color-mix(in srgb, var(--color-primario,#DB2777) 15%, transparent)' : 'none',
});

// Dibujito de cada plantilla (con la foto real si ya subiste una).
function Esquema({ plantilla, foto }) {
  const img = { background: foto ? `url("${optimizarFoto(foto, 240)}") center/cover` : 'linear-gradient(135deg,#F3C6D3,#E7A9BD)', borderRadius: '3px' };
  const linea = (w, h = 4, op = 1) => <div style={{ width: w, height: h, borderRadius: 2, background: '#2A2125', opacity: op, margin: '3px 0' }} />;
  const base = { height: '62px', borderRadius: '6px', overflow: 'hidden', background: '#F6EFE9', position: 'relative' };
  if (plantilla === 'dividida') return (
    <div style={{ ...base, display: 'grid', gridTemplateColumns: '1fr 1.2fr' }}>
      <div style={{ padding: '14px 8px' }}>{linea('90%', 6)}{linea('60%', 6)}{linea('70%', 3, .4)}</div><div style={img} />
    </div>);
  if (plantilla === 'velo') return (
    <div style={{ ...base, ...img, borderRadius: '6px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', padding: '8px' }}>
      <div style={{ width: '70%', height: 6, borderRadius: 2, background: '#fff', margin: '2px 0' }} /><div style={{ width: '45%', height: 3, borderRadius: 2, background: '#fff', opacity: .7 }} />
    </div>);
  if (plantilla === 'galeria') return (
    <div style={{ ...base, padding: '5px 5px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ ...img, width: '100%', height: '34px' }} />{linea('60%', 5)}{linea('40%', 3, .4)}
    </div>);
  return (
    <div style={{ ...base, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#E7A9BD', marginBottom: 3 }} />{linea('60%', 5)}{linea('40%', 3, .4)}
    </div>);
}

// Diseño de la tienda en línea: plantillas, colores, letras, textos y una
// vista previa en vivo (celular o computadora). Se guarda con "Guardar".
export default function DisenoTiendaConfig({ datos, set, guardando, portadaMostrada, onElegirPortada, onQuitarPortada }) {
  // Lo escrito se guarda tal cual mientras se edita (con sus espacios);
  // se limpia al guardar. La vista previa usa la versión limpia.
  const crudo = datos.tienda_diseno && typeof datos.tienda_diseno === 'object' ? datos.tienda_diseno : {};
  const d = normalizarDiseno(crudo);
  const cambiar = (campo, valor) => set('tienda_diseno', { ...crudo, [campo]: valor });
  const [vista, setVista] = useState('celular');
  const marcoRef = useRef(null);
  const [zoom, setZoom] = useState(0.5);

  // En "computadora" la tienda se dibuja a 1100 px y se achica para caber.
  useEffect(() => {
    const el = marcoRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => setZoom(Math.min(1, e.contentRect.width / 1100)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const tituloPortada = datos.catalogo_bienvenida?.trim() || `Bienvenida a ${datos.nombre || 'tu tienda'}`;
  const tienda = (
    <div className="cb cb-previa" style={{ ...variablesDiseno(d), '--cb-acento': datos.color_principal || '#DB2777' }}>
      {d.anuncio && <div className="cb-anuncio">{d.anuncio}</div>}
      <header className="cb-barra" style={{ position: 'relative' }}>
        <span className="cb-marca">
          {datos.logo_url && <img src={optimizarFoto(datos.logo_url, 120)} alt="" />}
          <span className="cb-serif">{datos.nombre}</span>
        </span>
        <span className="cb-icono">👜</span>
      </header>
      <PortadaTienda diseno={d} nombre={datos.nombre} logo={datos.logo_url} foto={portadaMostrada} titulo={tituloPortada} onVer={() => {}} />
    </div>
  );

  const Opciones = ({ lista, campo }) => (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {lista.map((o) => (
        <button key={o.valor} type="button" disabled={guardando} style={chip(d[campo] === o.valor)} onClick={() => cambiar(campo, o.valor)}>{o.nombre}</button>
      ))}
    </div>
  );

  return (
    <div style={caja}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700, fontSize: '15px' }}>🎨 Diseño de tu tienda en línea</div>
        <div style={{ display: 'flex', gap: '4px' }}>
          {[['celular', '📱 Celular'], ['computadora', '💻 Computadora']].map(([v, t]) => (
            <button key={v} type="button" style={chip(vista === v)} onClick={() => setVista(v)}>{t}</button>
          ))}
        </div>
      </div>
      <p style={{ margin: '4px 0 12px', fontSize: '12px', color: '#6B7280' }}>Elige cómo se ve lo primero que miran tus clientas. Los cambios se ven aquí al instante y se publican al tocar <b>Guardar</b>.</p>

      {/* Vista previa en vivo */}
      <div ref={marcoRef} style={{ borderRadius: '12px', border: '1px solid #E5E7EB', background: '#F3F4F6', padding: vista === 'celular' ? '14px 0' : 0, overflow: 'hidden' }}>
        {vista === 'celular' ? (
          <div style={{ width: '330px', maxWidth: '100%', margin: '0 auto', borderRadius: '18px', overflow: 'hidden', boxShadow: '0 6px 24px rgba(0,0,0,.12)', background: 'white' }}>{tienda}</div>
        ) : (
          <div style={{ width: '1100px', zoom }}>{tienda}</div>
        )}
      </div>

      <label style={titulo}>Plantilla de portada</label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '8px' }}>
        {PLANTILLAS.map((p) => (
          <button key={p.valor} type="button" disabled={guardando} style={tarjeta(d.plantilla === p.valor)} onClick={() => cambiar('plantilla', p.valor)} title={p.ayuda}>
            <Esquema plantilla={p.valor} foto={portadaMostrada} />
            <div style={{ marginTop: '6px', fontSize: '13px', fontWeight: 700, color: '#111827' }}>{p.nombre}</div>
            <div style={{ fontSize: '11px', color: '#6B7280', lineHeight: 1.35 }}>{p.ayuda}</div>
          </button>
        ))}
      </div>

      <label style={titulo}>Foto de portada</label>
      {portadaMostrada && (
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
          <img src={portadaMostrada.startsWith('blob:') ? portadaMostrada : optimizarFoto(portadaMostrada, 240)} alt="" style={{ width: '96px', height: '64px', objectFit: 'cover', borderRadius: '8px' }} />
          <button type="button" disabled={guardando} onClick={onQuitarPortada} style={chip(false)}>Quitar foto</button>
        </div>
      )}
      <input type="file" accept="image/*" disabled={guardando} style={{ fontSize: '13px' }} onChange={(e) => onElegirPortada(e.target.files[0])} />
      <p style={ayuda}>Mejor horizontal y con buena luz. {d.plantilla === 'texto' ? 'La plantilla "Solo texto" no muestra la foto.' : ''}</p>
      {portadaMostrada && d.plantilla !== 'texto' && (
        <>
          <label style={titulo}>¿Qué parte de la foto se ve más?</label>
          <Opciones lista={ENFOQUES} campo="enfoque" />
          <p style={ayuda}>Si se corta lo importante de tu foto, prueba con Arriba o Abajo.</p>
        </>
      )}

      <label style={titulo}>Color de fondo del texto</label>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {FONDOS.map((f) => (
          <button key={f.valor} type="button" disabled={guardando} onClick={() => cambiar('fondo', f.valor)}
            style={{ ...chip(d.fondo === f.valor), display: 'flex', alignItems: 'center', gap: '7px', '--cb-acento': datos.color_principal || '#DB2777' }}>
            <span style={{ width: '18px', height: '18px', borderRadius: '50%', background: f.muestra, border: '1px solid rgba(0,0,0,.12)' }} />{f.nombre}
          </button>
        ))}
      </div>
      {d.plantilla === 'velo' && <p style={ayuda}>En "Foto completa" las letras van blancas sobre tu foto; el fondo se usa si no hay foto.</p>}

      <label style={titulo}>Letra de los títulos</label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px' }}>
        {FUENTES.map((f) => (
          <button key={f.valor} type="button" disabled={guardando} style={tarjeta(d.fuente === f.valor)} onClick={() => cambiar('fuente', f.valor)}>
            <div style={{ fontFamily: f.familia, fontWeight: f.peso, fontSize: '22px', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{datos.nombre || 'Mi tienda'}</div>
            <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>{f.nombre}</div>
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0 16px' }}>
        <div><label style={titulo}>Botón</label><Opciones lista={BOTONES} campo="boton" /></div>
        <div><label style={titulo}>Alinear el texto</label><Opciones lista={ALINEACIONES} campo="alineacion" /></div>
        <div><label style={titulo}>Esquinas de las fotos</label><Opciones lista={ESQUINAS} campo="esquinas" /></div>
      </div>

      <label style={titulo}>Frase de bienvenida (título grande)</label>
      <input type="text" value={datos.catalogo_bienvenida} disabled={guardando} maxLength={140} style={input}
        onChange={(e) => set('catalogo_bienvenida', e.target.value)} placeholder={`Ej: Bienvenida a ${datos.nombre || 'nuestra tienda'} 💕`} />
      <p style={ayuda}>Si la dejas vacía, dice "Bienvenida a {datos.nombre || 'tu tienda'}". Las frases cortas se ven más elegantes.</p>

      <label style={titulo}>Texto debajo del título</label>
      <input type="text" value={crudo.subtitulo || ''} disabled={guardando} maxLength={160} style={input}
        onChange={(e) => cambiar('subtitulo', e.target.value)} placeholder={SUBTITULO_DEFECTO} />

      <label style={titulo}>Texto del botón</label>
      <input type="text" value={crudo.textoBoton || ''} disabled={guardando} maxLength={30} style={input}
        onChange={(e) => cambiar('textoBoton', e.target.value)} placeholder={`${BOTON_DEFECTO} (ej: Comprar ahora, Ver colección)`} />

      <label style={titulo}>Barra de anuncio (opcional)</label>
      <input type="text" value={crudo.anuncio || ''} disabled={guardando} maxLength={90} style={input}
        onChange={(e) => cambiar('anuncio', e.target.value)} placeholder="Ej: 🚚 Envíos a todo Honduras · Cambios en 7 días" />
      <p style={ayuda}>Una franja delgada arriba de tu tienda para promociones o envíos. Déjala vacía para no mostrarla.</p>
    </div>
  );
}
